import {
  normalizeServerUrl,
  extractToken,
  getOrCreateDeviceId,
} from './gatewayAuth'

export const CONTROLLER_STORAGE_KEYS = {
  URL: 'udv-controller-url',
  TOKEN: 'udv-controller-token',
  USER: 'udv-controller-user',
}

export const DEFAULT_CONTROLLER_URL = 'https://controller.tanjousoft.com.ar'

const HEALTH_TIMEOUT_MS = 4000

/**
 * URL del Controller: localStorage o REACT_APP_CONTROLLER_URL o default prod.
 * @returns {string}
 */
export function getControllerUrl() {
  try {
    const stored = window.localStorage.getItem(CONTROLLER_STORAGE_KEYS.URL)
    if (stored) return normalizeServerUrl(stored)
  } catch (e) {
    /* ignore */
  }
  const fromEnv =
    typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_CONTROLLER_URL
      ? String(process.env.REACT_APP_CONTROLLER_URL).trim()
      : ''
  if (fromEnv) {
    try {
      return normalizeServerUrl(fromEnv)
    } catch (e) {
      /* fall through */
    }
  }
  return DEFAULT_CONTROLLER_URL
}

/**
 * @param {string} url
 */
export function setControllerUrl(url) {
  const normalized = normalizeServerUrl(url)
  window.localStorage.setItem(CONTROLLER_STORAGE_KEYS.URL, normalized)
  return normalized
}

/**
 * @param {{ serverUrl: string, token: string, user?: object }} session
 */
export function saveControllerSession(session) {
  const serverUrl = normalizeServerUrl(session && session.serverUrl)
  const token = session && session.token ? String(session.token) : ''
  if (!token) {
    throw new Error('Token JWT Controller ausente')
  }
  window.localStorage.setItem(CONTROLLER_STORAGE_KEYS.URL, serverUrl)
  window.localStorage.setItem(CONTROLLER_STORAGE_KEYS.TOKEN, token)
  if (session.user != null) {
    window.localStorage.setItem(
      CONTROLLER_STORAGE_KEYS.USER,
      JSON.stringify(session.user)
    )
  }
  getOrCreateDeviceId()
}

/**
 * @returns {{ serverUrl: string, token: string, user: object|null, deviceId: string } | null}
 */
export function loadControllerSession() {
  try {
    const serverUrl =
      window.localStorage.getItem(CONTROLLER_STORAGE_KEYS.URL) ||
      getControllerUrl()
    const token = window.localStorage.getItem(CONTROLLER_STORAGE_KEYS.TOKEN)
    if (!token) return null
    let user = null
    const rawUser = window.localStorage.getItem(CONTROLLER_STORAGE_KEYS.USER)
    if (rawUser) {
      try {
        user = JSON.parse(rawUser)
      } catch (e) {
        user = null
      }
    }
    return {
      serverUrl: normalizeServerUrl(serverUrl),
      token,
      user,
      deviceId: getOrCreateDeviceId(),
    }
  } catch (e) {
    return null
  }
}

/** Cierra sesión Controller. Conserva DeviceID y URL preferida. */
export function clearControllerSession() {
  try {
    window.localStorage.removeItem(CONTROLLER_STORAGE_KEYS.TOKEN)
    window.localStorage.removeItem(CONTROLLER_STORAGE_KEYS.USER)
  } catch (e) {
    /* ignore */
  }
}

/**
 * @param {{ token?: string } | null} [session]
 * @returns {{ Authorization?: string }}
 */
export function authHeadersController(session) {
  const s = session || loadControllerSession()
  if (!s || !s.token) return {}
  return { Authorization: `Bearer ${s.token}` }
}

/**
 * GET {url}/api/v1/health — acepta 2xx o JSON status ok.
 * @param {string} [serverUrl]
 * @returns {Promise<{ ok: boolean, reason?: string, status?: number }>}
 */
export async function checkControllerHealth(serverUrl) {
  let url
  try {
    if (serverUrl) {
      url = normalizeServerUrl(serverUrl)
    } else {
      const session = loadControllerSession()
      url = (session && session.serverUrl) || getControllerUrl()
    }
  } catch (e) {
    return { ok: false, reason: 'no_url' }
  }

  let abortCtrl = null
  let timer = null
  try {
    const opts = { method: 'GET' }
    if (typeof AbortController !== 'undefined') {
      abortCtrl = new AbortController()
      opts.signal = abortCtrl.signal
      timer = setTimeout(function () {
        try {
          abortCtrl.abort()
        } catch (e) {
          /* ignore */
        }
      }, HEALTH_TIMEOUT_MS)
    }
    const res = await fetch(`${url}/api/v1/health`, opts)
    if (!res.ok) {
      return { ok: false, reason: 'http', status: res.status }
    }
    try {
      const data = await res.json()
      if (
        data &&
        typeof data === 'object' &&
        data.status != null &&
        String(data.status).toLowerCase() !== 'ok' &&
        data.status !== true
      ) {
        return { ok: false, reason: 'status', status: res.status }
      }
    } catch (e) {
      /* 2xx sin JSON sigue siendo ok */
    }
    return { ok: true, status: res.status }
  } catch (e) {
    const aborted =
      e &&
      (e.name === 'AbortError' ||
        (typeof e.message === 'string' && /abort/i.test(e.message)))
    return { ok: false, reason: aborted ? 'timeout' : 'network' }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Normaliza "0.19.5-dev" / "v0.19.5" → [0, 19, 5]
 * @param {string} version
 * @returns {number[]}
 */
export function parseUdvSemverParts(version) {
  const raw = String(version == null ? '' : version)
    .trim()
    .replace(/^v/i, '')
  const core = raw.split(/[-+]/)[0] || ''
  const parts = core.split('.').map((p) => parseInt(p, 10) || 0)
  while (parts.length < 3) parts.push(0)
  return parts.slice(0, 3)
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {number} negativo si a < b, 0 si igual, positivo si a > b
 */
export function compareUdvSemver(a, b) {
  const pa = parseUdvSemverParts(a)
  const pb = parseUdvSemverParts(b)
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] - pb[i]
  }
  return 0
}

/**
 * @param {string} localVersion
 * @param {string} udvMin
 * @returns {boolean}
 */
export function isUdvBelowMin(localVersion, udvMin) {
  if (!udvMin) return false
  return compareUdvSemver(localVersion, udvMin) < 0
}

/**
 * GET {url}/api/v1/compat
 * @param {string} [serverUrl]
 * @returns {Promise<{
 *   ok: boolean,
 *   reason?: string,
 *   udv_min?: string,
 *   udv_recommended?: string,
 *   edge_min?: string,
 *   features?: string[],
 *   controller_version?: string,
 *   matrix?: object[],
 *   raw?: object,
 * }>}
 */
export async function fetchControllerCompat(serverUrl) {
  let url
  try {
    if (serverUrl) {
      url = normalizeServerUrl(serverUrl)
    } else {
      const session = loadControllerSession()
      url = (session && session.serverUrl) || getControllerUrl()
    }
  } catch (e) {
    return { ok: false, reason: 'no_url' }
  }

  let abortCtrl = null
  let timer = null
  try {
    const opts = { method: 'GET' }
    if (typeof AbortController !== 'undefined') {
      abortCtrl = new AbortController()
      opts.signal = abortCtrl.signal
      timer = setTimeout(function () {
        try {
          abortCtrl.abort()
        } catch (e) {
          /* ignore */
        }
      }, HEALTH_TIMEOUT_MS)
    }
    const res = await fetch(`${url}/api/v1/compat`, opts)
    if (!res.ok) {
      return { ok: false, reason: 'http', status: res.status }
    }
    const data = await res.json()
    return {
      ok: true,
      udv_min: data.udv_min || (data.compat && data.compat.udv_min),
      udv_recommended:
        data.udv_recommended ||
        (data.compat && data.compat.udv_recommended),
      edge_min: data.edge_min || (data.compat && data.compat.edge_min),
      features: data.features || (data.compat && data.compat.features) || [],
      controller_version:
        data.controller_version || data.version || undefined,
      matrix: data.matrix || [],
      raw: data,
    }
  } catch (e) {
    const aborted =
      e &&
      (e.name === 'AbortError' ||
        (typeof e.message === 'string' && /abort/i.test(e.message)))
    return { ok: false, reason: aborted ? 'timeout' : 'network' }
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * POST {controllerUrl}/api/v1/auth/google
 * @param {string} controllerUrl
 * @param {string} idToken
 */
export async function loginWithGoogle(controllerUrl, idToken) {
  const url = setControllerUrl(controllerUrl || getControllerUrl())
  const credential = String(idToken || '').trim()
  if (!credential) {
    throw new Error('ID token de Google vacío')
  }

  let res
  try {
    res = await fetch(`${url}/api/v1/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: credential }),
    })
  } catch (e) {
    throw new Error(
      `No se pudo conectar al Controller: ${e && e.message ? e.message : e}`
    )
  }

  if (res.status === 401) {
    throw new Error('Google Sign-In rechazado por el Controller')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(
      `Auth Google falló (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`
    )
  }

  let data
  try {
    data = await res.json()
  } catch (e) {
    throw new Error('El Controller no devolvió JSON en auth/google')
  }

  const token = extractToken(data)
  if (!token) {
    throw new Error('El Controller no devolvió un token JWT')
  }

  const user = data.user || {
    email: data.email,
    full_name: data.full_name || data.name,
    id: data.user_id || data.id,
  }

  saveControllerSession({ serverUrl: url, token, user })
  return {
    serverUrl: url,
    token,
    user,
    deviceId: getOrCreateDeviceId(),
    expiresIn: data.expires_in,
    raw: data,
  }
}

/**
 * GET /api/v1/auth/me
 * @param {{ session?: object }} [options]
 */
export async function fetchMe(options) {
  const opts = options || {}
  const session = opts.session || loadControllerSession()
  if (!session || !session.token) {
    throw new Error('No hay sesión de Controller')
  }
  const res = await fetch(`${session.serverUrl}/api/v1/auth/me`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...authHeadersController(session),
    },
  })
  if (res.status === 401) {
    throw new Error('Sesión Controller inválida o expirada')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(
      `auth/me falló (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`
    )
  }
  const data = await res.json()
  const user = data.user || data
  if (user && typeof user === 'object') {
    saveControllerSession({
      serverUrl: session.serverUrl,
      token: session.token,
      user,
    })
  }
  return data
}
